import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  Tenant,
  CreateTenantRequest,
  UpdateTenantRequest,
  TenantResources,
  TenantActivity,
  ProvisioningTimeoutInfo,
  ProductCode,
  ProductInfo,
  AuditLogQueryOptions,
  AuditLogResponse,
  UpdateRetentionRequest,
  UpdateRetentionResponse,
  PaginatedResponse,
  PaginationParams,
  TenantListParams,
} from '../types';
import {
  TenantConfigurationResponse,
  FixConfigurationRequest,
  FixConfigurationResponse,
  ConfigurationHealthBatchResponse,
  UpgradeHistoryResponse,
  UpgradeHistoryOptions,
  VersionSummary,
  TenantHealthStatus,
  TenantHealthCheck,
  HealthCheckType,
  ResourceHealthStatus,
  PltS2SServiceHealthResponse,
  TenantCostSummary,
  CostHistoryOptions,
  CostHistoryResponse,
  // Aliased as CommonPaginatedResponse to distinguish from the local PaginatedResponse
  // type imported from '../types'. Both types have identical structure but are defined
  // separately for package isolation.
  PaginatedResponse as CommonPaginatedResponse,
  MagicLinkStatus,
  RevokeMagicLinkResponse,
  CurrentLifecycleResponse,
  CanonicalLifecycleHistoryItem,
  CanonicalLifecycleOperation,
  ApplicationRelease,
  PltDataStatusResponse,
  PltDataOperationsQuery,
  PltDataOperationsResponse,
  AlertQuery,
  AlertScope,
  CentralizedAlertsResponse,
  alertSourceForScope,
} from '@internal/plugin-tenant-management-common';
import { generateCorrelationId } from '../utils/errorUtils';

export type PendingUpgradeStatus =
  'ready' | 'forced' | 'held' | 'window_conflict' | 'rate_capped';

export interface PendingUpgrade {
  tenantId: string;
  tenantName: string | null;
  cohort: string;
  product: string;
  currentVersion: string | null;
  targetVersion: string;
  releaseId: string;
  rawScheduledAt: string;
  effectiveScheduledAt: string;
  status: PendingUpgradeStatus;
  reason: string;
  policySource: string;
  overrideId: string | null;
}

export interface PendingUpgradesResponse {
  items: PendingUpgrade[];
  nextCursor: string | null;
}

export interface PendingUpgradeListOptions {
  cohort?: string;
  product?: string;
  status?: PendingUpgradeStatus | string;
  release?: string;
  limit?: number;
  cursor?: string;
}

export type BulkUpgradeJobStatus =
  'pending' | 'running' | 'recovering' | 'paused' | 'completed' | 'failed';

export interface BulkUpgradeJob {
  id: string;
  cohort: string;
  targetVersion: string;
  releaseId: string | null;
  batchSize: number;
  continueOnFailure: boolean;
  status: BulkUpgradeJobStatus;
  totalTenants: number;
  startedCount: number;
  failedCount: number;
  requestedBy: string;
  reason: string;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  skipped: Array<{ tenantId: string; reason: string }>;
}

export interface StartBulkUpgradeRequest {
  cohort: string;
  targetVersion: string;
  releaseId?: string | null;
  productCodes?: string[];
  batchSize?: number;
  continueOnFailure?: boolean;
  reason: string;
}

export interface StartBulkUpgradeResult {
  job: BulkUpgradeJob;
  started: Array<{ tenantId: string; product: string }>;
  skipped: Array<{ tenantId: string; reason: string }>;
}

export interface AutoRollbackRequest {
  cohort: string;
  targetVersion: string;
  releaseId?: string | null;
  productCodes?: string[];
  reason: string;
  dryRun?: boolean;
}

export interface AutoRollbackResult {
  dryRun: boolean;
  promotionPaused: boolean;
  candidates: Array<{
    historyId: string;
    tenantId: string;
    fromVersion: string;
    toVersion: string;
    releaseId: string | null;
    productCode: string | null;
    errorMessage: string | null;
  }>;
  dispatched: Array<{
    tenantId: string;
    rollbackVersion: string;
    failedHistoryId: string;
  }>;
  skipped: Array<{
    tenantId: string;
    failedHistoryId: string;
    reason: string;
  }>;
}

/**
 * API interface for tenant management operations
 */
export interface TenantManagementApi {
  /**
   * List all available products
   */
  listProducts(): Promise<ProductInfo[]>;

  /**
   * Get a specific product by code
   */
  getProduct(code: ProductCode): Promise<ProductInfo>;

  /**
   * List all tenants with pagination, filtering, and optional enrichment.
   *
   * @remarks
   * Use the `include` parameter to request additional computed fields:
   * - 'configHealth': Configuration health summary (drift detection)
   * - 'upgradeInfo': Upgrade availability information
   *
   * This consolidates N+2 API calls into a single call for the tenant list page.
   *
   * @param options - Pagination, filter, and enrichment options
   * @returns Paginated response with tenants (optionally enriched)
   */
  listTenants(options?: TenantListParams): Promise<PaginatedResponse<Tenant>>;

  /**
   * Get a specific tenant by ID
   */
  getTenant(id: string): Promise<Tenant>;

  /**
   * Create a new tenant
   */
  createTenant(request: CreateTenantRequest): Promise<Tenant>;

  /**
   * Update an existing tenant
   */
  updateTenant(id: string, request: UpdateTenantRequest): Promise<Tenant>;

  /** Legacy public delete action: deprovision infrastructure and retain data. */
  deleteTenant(id: string): Promise<void>;
  /** Dispatch canonical delete for a deprovisioned tenant. */
  deleteDeprovisionedTenant(id: string): Promise<Tenant>;

  /**
   * Hard delete a tenant (permanent removal from database)
   */
  hardDeleteTenant(id: string): Promise<void>;

  /**
   * List all deleted tenants with pagination (Issue #266, #12)
   * @param options - Pagination options (limit, offset)
   * @returns Paginated response with deleted tenants
   */
  listDeletedTenants(
    options?: PaginationParams,
  ): Promise<PaginatedResponse<Tenant>>;

  /**
   * Check if a tenant is eligible for hard delete
   * Issue #327: Hard delete is a super admin DB cleanup function with no prerequisites
   * Always returns eligible=true if tenant exists (permission check is separate)
   */
  getHardDeleteEligibility(id: string): Promise<{
    eligible: boolean;
    reason?: string;
  }>;

  /**
   * Restore a deleted tenant (Issue #266)
   * Clears deleted_at and triggers re-provisioning workflow
   * WARNING: Previous infrastructure data may be lost
   */
  restoreTenant(id: string): Promise<Tenant>;

  /**
   * Suspend a tenant
   */
  suspendTenant(id: string): Promise<Tenant>;

  /**
   * Resume a suspended tenant
   */
  resumeTenant(id: string): Promise<Tenant>;

  /**
   * Get tenant resources
   */
  getTenantResources(id: string): Promise<TenantResources>;

  /**
   * Provision a tenant (initial provisioning or retry after failure)
   *
   * Triggers the GitHub Actions provisioning workflow for the tenant's product.
   * When retrying a failed provision, an optional targetVersion can be specified
   * to attempt provisioning with a different release version.
   *
   * @param id - Tenant UUID
   * @param targetVersion - Optional target release version for retry provisioning (e.g., 'v1.2.0').
   *   If not provided, uses the version from tenant metadata.
   * @returns The updated tenant in 'Provisioning' transitional state
   * @throws NotFoundError - If tenant doesn't exist
   * @throws ConflictError - If tenant is already provisioned or has an operation in progress
   */
  provisionTenant(id: string, targetVersion?: string): Promise<Tenant>;

  /**
   * Re-provision an existing tenant, re-running the full provisioning workflow.
   *
   * Unlike provisionTenant(), this is permitted against a healthy Active
   * ('provisioned') tenant -- which provisionTenant() rejects with 'Tenant is
   * already provisioned'. It re-runs Terraform against a live tenant, so it is a
   * distinct endpoint rather than a flag on provision.
   *
   * @param id - Tenant UUID
   * @param targetVersion - Optional target release version (e.g., 'v1.2.0').
   *   If not provided, uses the tenant's current release.
   * @returns The updated tenant in 'Provisioning' transitional state
   * @throws NotFoundError - If tenant doesn't exist
   * @throws ConflictError - If an operation is already in progress, or the tenant's
   *   state is not one of reprovision.requiredState
   */
  reprovisionTenant(id: string, targetVersion?: string): Promise<Tenant>;

  /**
   * Retry a failed lifecycle operation (Phase 3 Plan 3).
   *
   * @param id - Tenant UUID
   * @param operation - The lifecycle operation to retry
   * @param versionId - Optional version for version-dependent operations
   * @returns The updated tenant
   */
  retryOperation(
    id: string,
    operation: CanonicalLifecycleOperation,
    versionId?: string,
  ): Promise<Tenant>;

  /**
   * Upgrade a tenant to a new release version (Issue #341)
   *
   * Triggers the provisioning workflow with a new version. For serverless products,
   * this redeploys the application without recreating infrastructure.
   *
   * @param id - Tenant ID
   * @param targetVersion - Target release version (e.g., 'v1.2.0')
   * @param upgradeType - Type of upgrade (default: 'upgrade')
   * @returns Upgrade initiation response with upgrade history ID
   */
  upgradeTenant(
    id: string,
    targetVersion: string,
    upgradeType?: 'upgrade' | 'rollback' | 'forced' | 'hotfix',
  ): Promise<{
    message: string;
    upgradeId: string;
    tenantId: string;
    fromVersion: string | null;
    toVersion: string;
    status: string;
  }>;

  /**
   * Trigger a software-only upgrade for a PLT tenant.
   *
   * This triggers the plt-software-upgrade.yml workflow which redeploys
   * the application (frontend + API) without running Terraform.
   * This is significantly faster than the full upgrade workflow.
   *
   * @param id - Tenant ID
   * @param targetVersion - Target release version (e.g., 'v1.2.0')
   * @returns Upgrade initiation response with upgrade history ID
   */
  softwareUpgradeTenant(
    id: string,
    targetVersion: string,
  ): Promise<{
    message: string;
    upgradeId: string;
    tenantId: string;
    fromVersion: string | null;
    toVersion: string;
    status: string;
  }>;

  /**
   * Get tenant activity log with pagination (Issue #12)
   * @param id - Tenant ID
   * @param options - Pagination options (limit, offset)
   * @returns Paginated response with activity entries
   */
  getTenantActivity(
    id: string,
    options?: PaginationParams,
  ): Promise<PaginatedResponse<TenantActivity>>;

  /**
   * Get GitHub workflow runs for tenant with pagination (Issue #12)
   * @param id - Tenant ID
   * @param options - Pagination options (limit, offset)
   * @returns Paginated response with workflow runs
   */
  getTenantWorkflowRuns(
    id: string,
    options?: PaginationParams,
  ): Promise<PaginatedResponse<CanonicalLifecycleHistoryItem>>;

  /**
   * Cancel a running workflow for a tenant.
   * This is a destructive action that can leave the tenant in an inconsistent state.
   * Should only be used when a workflow is stuck and needs manual intervention.
   *
   * @param id - Tenant ID
   * @param forceCancel - If true, force cancel without waiting for in-progress steps
   * @returns Result with success status and warning message
   */
  cancelWorkflow(
    id: string,
    forceCancel?: boolean,
  ): Promise<{
    success: boolean;
    message: string;
    warning?: string;
    error?: string;
  }>;

  /**
   * Get tenant management configuration from the backend.
   * This includes polling intervals and other runtime configuration.
   */
  getConfig(): Promise<{
    polling: {
      intervalMs: number;
    };
  }>;

  /**
   * Get retention defaults for all tiers (Issue #299).
   * Used by RetentionSettingsCard to display tier-based default retention periods.
   */
  getRetentionDefaults(): Promise<{
    tierDefaults: Record<string, number>;
    globalDefault: number;
  }>;

  /**
   * Get consolidated non-lifecycle refresh data for a tenant. Lifecycle state
   * is fetched only from getCurrentLifecycle().
   *
   * @param id - Tenant ID
   * @returns Combined polling data
   */
  getPollingStatus(id: string): Promise<{
    tenant: Tenant;
    activities: TenantActivity[];
    timeoutInfo: ProvisioningTimeoutInfo | null;
  }>;

  /**
   * Get the authoritative lifecycle attempt and tenant lifecycle summary.
   * A dispatching attempt is returned before GitHub attaches a workflow run.
   */
  getCurrentLifecycle(id: string): Promise<CurrentLifecycleResponse>;

  // ============================================================
  // LIFECYCLE OPERATIONS (Issue #299)
  // ============================================================

  /**
   * Deprovision a tenant - removes infrastructure but preserves data backups.
   * The tenant will transition to 'Deprovisioned' when the workflow completes.
   * Data backups are retained according to the tenant's retention policy.
   */
  deprovisionTenant(id: string, reason?: string): Promise<Tenant>;

  /**
   * Update tenant retention settings.
   * Requires a reason for audit compliance.
   */
  updateRetention(
    id: string,
    request: UpdateRetentionRequest,
  ): Promise<UpdateRetentionResponse>;

  /**
   * Get audit log entries for a tenant.
   * Returns a paginated list of all changes to the tenant for compliance purposes.
   */
  getAuditLog(
    id: string,
    options?: AuditLogQueryOptions,
  ): Promise<AuditLogResponse>;

  // ============================================================
  // VERSION TRACKING (Issue #340, #341)
  // ============================================================

  /**
   * Get upgrade history for a tenant.
   * Returns a paginated list of all version changes including provisioning,
   * upgrades, rollbacks, and hotfixes.
   *
   * @param id - Tenant ID
   * @param options - Filter and pagination options
   * @returns Paginated upgrade history records
   */
  getUpgradeHistory(
    id: string,
    options?: UpgradeHistoryOptions,
  ): Promise<UpgradeHistoryResponse>;

  /**
   * Get version distribution summary across all tenants.
   * Returns a breakdown of how many tenants are running each version.
   *
   * @returns Version summary with counts and percentages
   */
  getVersionSummary(): Promise<{ versions: VersionSummary[] }>;

  /**
   * List the current fleet-wide pending rollout view.
   */
  listPendingUpgrades(
    options?: PendingUpgradeListOptions,
  ): Promise<PendingUpgradesResponse>;

  /**
   * Start a bulk upgrade job for a rollout cohort.
   */
  startBulkUpgrade(
    request: StartBulkUpgradeRequest,
  ): Promise<StartBulkUpgradeResult>;

  /**
   * Get the latest state for a bulk upgrade job.
   */
  getBulkUpgradeJob(jobId: string): Promise<BulkUpgradeJob>;

  /**
   * Pause a bulk upgrade job that is still running.
   */
  pauseBulkUpgradeJob(jobId: string): Promise<BulkUpgradeJob>;

  /**
   * Resume a paused bulk upgrade job.
   */
  resumeBulkUpgradeJob(jobId: string): Promise<BulkUpgradeJob>;

  /**
   * Evaluate or dispatch rollback for failed tenants in a cohort rollout.
   */
  rollbackFailedCohort(
    request: AutoRollbackRequest,
  ): Promise<AutoRollbackResult>;

  /**
   * Sync releases from GitHub for a specific product.
   * Fetches releases from the product's configured GitHub repository
   * and stores them in the application_releases table.
   *
   * @param productCode - Product code (e.g., 'PLT')
   * @returns Sync statistics
   */
  syncReleasesFromProduct(productCode: ProductCode): Promise<{
    synced: number;
    skipped: number;
    productCode: ProductCode;
    repository: string;
  }>;

  /**
   * Sync releases from GitHub for all active products.
   * Iterates through all products and syncs their releases.
   *
   * @returns Array of sync results for each product
   */
  syncAllProductReleases(): Promise<{
    results: Array<{
      synced: number;
      skipped: number;
      productCode: ProductCode;
      repository: string;
      error?: string;
    }>;
  }>;

  /**
   * List the most recent published releases for a product, newest first.
   * Excludes prereleases and drafts. Used by the homepage Platform
   * Releases card.
   *
   * @param productCode - Product code (e.g., 'PLT')
   * @param limit - Maximum releases to return; server clamps to 1-20
   */
  listProductReleases(
    productCode: ProductCode,
    limit?: number,
  ): Promise<ApplicationRelease[]>;

  /**
   * Check if an upgrade is available for a tenant.
   * Compares the tenant's current version against the latest available release.
   *
   * @param id - Tenant ID
   * @returns Upgrade availability info
   */
  checkUpgradeAvailable(id: string): Promise<{
    upgradeAvailable: boolean;
    currentVersion?: string;
    latestVersion?: string;
    latestReleaseId?: string;
    productCode?: ProductCode;
  }>;

  /**
   * Verify tenant version by comparing database version with Azure resource tags.
   * Queries Azure resource tags to detect if the deployed version matches the database.
   * Useful for detecting cases where workflow callbacks failed but deployment succeeded.
   *
   * @param id - Tenant ID
   * @returns Version verification result
   */
  verifyTenantVersion(id: string): Promise<{
    tenantId: string;
    databaseVersion: string | null;
    azureVersion: string | null;
    match: boolean;
    driftDescription?: string;
    verifiedResourceId?: string;
    errorMessage?: string;
    verifiedAt: string;
  }>;

  /**
   * Get the latest release for a product.
   * Returns the most recent non-prerelease, non-draft release.
   *
   * @param productCode - Product code (e.g., 'PLT')
   * @returns Latest release info or undefined if none found
   */
  getLatestRelease(productCode: ProductCode): Promise<
    | {
        id: string;
        version: string;
        tagName: string;
        releaseName?: string;
        publishedAt: string;
        prerelease: boolean;
      }
    | undefined
  >;

  // ============================================================
  // MAGIC LINK OPERATIONS (Issue #300)
  // ============================================================

  /**
   * Send a magic link to a tenant administrator for SSO configuration.
   * Generates a secure token, stores the hash in Admin Portal PostgreSQL,
   * and sends an email via Azure Communication Services SDK.
   *
   * @param id - Tenant ID
   * @param recipientEmail - Email address to send the magic link to
   * @returns Promise with success status, correlation ID, and expiration
   */
  sendMagicLink(
    id: string,
    recipientEmail: string,
  ): Promise<{
    success: boolean;
    message: string;
    correlationId?: string;
    expiresAt?: string;
  }>;

  /**
   * Get the current magic link status for a tenant.
   *
   * @param id - Tenant ID
   * @returns Promise with magic link status (exists, expired, used, etc.)
   */
  getMagicLinkStatus(id: string): Promise<MagicLinkStatus>;

  /**
   * Revoke any active magic link for a tenant.
   *
   * @param id - Tenant ID
   * @returns Promise with revocation result
   */
  revokeMagicLink(id: string): Promise<RevokeMagicLinkResponse>;

  // ============================================================
  // CONFIGURATION DRIFT DETECTION (Issue #322)
  // ============================================================

  /**
   * Get tenant configuration with drift detection.
   * Compares current configuration against product/tier defaults.
   */
  getConfiguration(id: string): Promise<TenantConfigurationResponse>;

  /**
   * Fix configuration drift for a tenant.
   * Can fix all drifted fields or specific fields by key.
   */
  fixConfiguration(
    id: string,
    request: FixConfigurationRequest,
  ): Promise<FixConfigurationResponse>;

  /**
   * Get configuration health for multiple tenants (batch endpoint).
   * Returns lightweight health summaries for efficient list view display.
   */
  getConfigurationHealthBatch(
    tenantIds: string[],
  ): Promise<ConfigurationHealthBatchResponse>;

  // ============================================================
  // ADMIN OPERATIONS
  // ============================================================

  /**
   * Admin reset of tenant status - allows overriding stuck tenants.
   * This is an emergency operation that bypasses normal state transitions.
   * Should only be used when a tenant is stuck due to workflow callback failures.
   *
   * @param id - Tenant ID
   * @param reason - Reason for the reset (min 10 characters, for audit trail)
   * @returns Promise with success status, updated tenant, and message
   */
  adminResetTenantStatus(
    id: string,
    reason: string,
  ): Promise<{
    success: boolean;
    tenant: Tenant;
    message: string;
  }>;

  // ============================================================
  // HEALTH MONITORING (Issue #359)
  // ============================================================

  /**
   * Get current health status for a tenant.
   * Returns the aggregated health status from the most recent checks.
   *
   * @param id - Tenant ID
   * @returns Current health status or null if no checks have been run
   */
  getHealthStatus(id: string): Promise<TenantHealthStatus | null>;

  /**
   * Get health check history for a tenant.
   * Returns paginated historical health check results.
   *
   * @param id - Tenant ID
   * @param options - Pagination and filter options
   * @returns Paginated health check history
   */
  getHealthHistory(
    id: string,
    options?: {
      limit?: number;
      offset?: number;
      checkType?: HealthCheckType;
    },
  ): Promise<CommonPaginatedResponse<TenantHealthCheck>>;

  /**
   * Get resource-level health status for a tenant.
   * Returns health status for each Azure resource in tenant_resources.
   *
   * @param id - Tenant ID
   * @returns Array of resource health statuses
   */
  getResourceHealthStatus(id: string): Promise<ResourceHealthStatus[]>;

  /**
   * Get the provisioning-backed PLT S2S capability and latest service health
   * snapshot. This remains separate from legacy app/Azure overall health.
   */
  getPltS2SServiceHealth(id: string): Promise<PltS2SServiceHealthResponse>;

  /** Capability, latest data-status snapshot, and derived readiness rollup. */
  getPltDataStatus(id: string): Promise<PltDataStatusResponse>;

  /**
   * One bounded, live page of PLT backfill/reconciliation evidence. Nothing
   * is cached by the Portal; follow `page.nextCursor` until it is null.
   */
  getPltDataOperations(
    id: string,
    query: PltDataOperationsQuery,
  ): Promise<PltDataOperationsResponse>;

  /** Query an authorized, bounded page of centralized Azure Monitor alerts. */
  queryCentralizedAlerts(
    input: AlertQuery,
    signal?: AbortSignal,
  ): Promise<CentralizedAlertsResponse>;

  /**
   * Trigger a manual health check for a tenant.
   * Runs both application and infrastructure health checks immediately.
   *
   * @param id - Tenant ID
   * @returns Updated health status after the check
   */
  triggerHealthCheck(id: string): Promise<TenantHealthStatus>;

  /**
   * Refresh health checks for all active tenants.
   * Called when user clicks the Refresh button on the tenant list page.
   *
   * @returns Success indicator
   */
  refreshAllHealthChecks(): Promise<{ success: boolean; message: string }>;

  // ============================================================
  // COST MONITORING (Issue #271)
  // ============================================================

  /**
   * Get current cost summary for a tenant.
   * Returns the aggregated cost data including MTD, daily average, and trend.
   *
   * @param id - Tenant ID
   * @returns Current cost summary or null if no cost data exists
   */
  getCostSummary(id: string): Promise<TenantCostSummary | null>;

  /**
   * Get cost history for a tenant.
   * Returns paginated historical cost records.
   *
   * @param id - Tenant ID
   * @param options - Pagination and filter options
   * @returns Paginated cost history
   */
  getCostHistory(
    id: string,
    options?: CostHistoryOptions,
  ): Promise<CostHistoryResponse>;

  /**
   * Trigger a manual cost refresh for a tenant.
   * Fetches latest cost data from Azure Cost Management.
   *
   * @param id - Tenant ID
   * @returns Updated cost summary after the refresh
   */
  refreshTenantCosts(id: string): Promise<TenantCostSummary>;
}

/**
 * API reference for tenant management
 */
export const tenantManagementApiRef = createApiRef<TenantManagementApi>().with({
  id: 'plugin.tenant-management',
});

const ALERT_WINDOWS = new Set(['24h', '7d', '30d']);
const ALERT_SOURCE_IDS = new Set(['teseras', 'production']);
const ALERT_SCOPES = new Set(['test', 'staging', 'production', 'development']);
const ALERT_DETAIL_LABELS = new Set(['Host', 'WAF rule']);
const ALERT_ERROR_CODES = new Set([
  'configuration_error',
  'unauthorized',
  'rate_limited',
  'timeout',
  'invalid_response',
  'query_failed',
]);
const ALERT_SEVERITIES = new Set(['Sev0', 'Sev1', 'Sev2', 'Sev3', 'Sev4']);
const ALERT_CONDITIONS = new Set(['Fired', 'Resolved']);
const ALERT_STATES = new Set(['New', 'Acknowledged', 'Closed']);
const ALERT_ENVIRONMENTS = new Set([
  'test',
  'staging',
  'production',
  'development',
  'shared-non-production',
  'unattributed',
]);
const ALERT_QUERY_ERROR_CODES = new Set([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'INVALID_QUERY',
  'INVALID_CURSOR',
  'REFRESH_REQUIRED',
  'WINDOW_TOO_LARGE',
  'REQUEST_TOO_LARGE',
  'configuration_error',
  'QUERY_FAILED',
]);

export type CentralizedAlertsQueryErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'INVALID_QUERY'
  | 'INVALID_CURSOR'
  | 'REFRESH_REQUIRED'
  | 'WINDOW_TOO_LARGE'
  | 'REQUEST_TOO_LARGE'
  | 'configuration_error'
  | 'QUERY_FAILED';

export class CentralizedAlertsQueryError extends Error {
  constructor(
    readonly code: CentralizedAlertsQueryErrorCode,
    readonly statusCode: number,
    message: string,
  ) {
    super(message);
    this.name = 'CentralizedAlertsQueryError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isTimestamp(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function hasAlertClassification(value: Record<string, unknown>): boolean {
  return (
    typeof value.severity === 'string' &&
    ALERT_SEVERITIES.has(value.severity) &&
    typeof value.monitorCondition === 'string' &&
    ALERT_CONDITIONS.has(value.monitorCondition) &&
    typeof value.alertState === 'string' &&
    ALERT_STATES.has(value.alertState) &&
    typeof value.environment === 'string' &&
    ALERT_ENVIRONMENTS.has(value.environment)
  );
}

function hasAlertTimes(value: Record<string, unknown>): boolean {
  return (
    isTimestamp(value.firedAt) &&
    isTimestamp(value.lastModifiedAt) &&
    Date.parse(value.firedAt) <= Date.parse(value.lastModifiedAt)
  );
}

function hasAlertTargets(value: Record<string, unknown>): boolean {
  return (
    typeof value.targetResourceId === 'string' &&
    typeof value.targetResourceName === 'string' &&
    typeof value.alertUrl === 'string' &&
    typeof value.resourceUrl === 'string' &&
    (value.logsUrl === null || typeof value.logsUrl === 'string') &&
    (value.matchingLogsUrl === null ||
      typeof value.matchingLogsUrl === 'string')
  );
}

function hasAlertTraceability(value: Record<string, unknown>): boolean {
  return (
    (value.description === null || typeof value.description === 'string') &&
    Array.isArray(value.details) &&
    value.details.every(
      detail =>
        isRecord(detail) &&
        typeof detail.label === 'string' &&
        ALERT_DETAIL_LABELS.has(detail.label) &&
        typeof detail.value === 'string',
    )
  );
}

function isCentralizedAlert(value: unknown, sourceId: string): boolean {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.sourceId === 'string' &&
    value.sourceId === sourceId &&
    typeof value.ruleName === 'string' &&
    hasAlertClassification(value) &&
    hasAlertTimes(value) &&
    hasAlertTargets(value) &&
    hasAlertTraceability(value)
  );
}

function hasAlertResponseEnvelope(value: Record<string, unknown>): boolean {
  return (
    value.schemaVersion === 2 &&
    typeof value.scope === 'string' &&
    ALERT_SCOPES.has(value.scope) &&
    typeof value.window === 'string' &&
    ALERT_WINDOWS.has(value.window) &&
    isTimestamp(value.from) &&
    isTimestamp(value.to) &&
    isTimestamp(value.expiresAt) &&
    Date.parse(value.from) <= Date.parse(value.to) &&
    Date.parse(value.to) < Date.parse(value.expiresAt) &&
    // A Portal reads exactly one source: its own subscription.
    Array.isArray(value.sources) &&
    value.sources.length === 1 &&
    (value.nextCursor === null || typeof value.nextCursor === 'string')
  );
}

function hasValidSourceShape(source: Record<string, unknown>): boolean {
  const sourceId = source.sourceId;
  const items = source.items;
  return (
    typeof sourceId === 'string' &&
    ALERT_SOURCE_IDS.has(sourceId) &&
    (source.status === 'success' || source.status === 'error') &&
    isTimestamp(source.queriedAt) &&
    (source.lastSuccessfulAt === null ||
      isTimestamp(source.lastSuccessfulAt)) &&
    (source.pageKey === null || typeof source.pageKey === 'string') &&
    Array.isArray(items) &&
    items.every(item => isCentralizedAlert(item, sourceId)) &&
    Number.isSafeInteger(source.unattributedCount) &&
    (source.unattributedCount as number) >= 0 &&
    typeof source.exhausted === 'boolean'
  );
}

function hasValidSourceState(source: Record<string, unknown>): boolean {
  if (
    source.lastSuccessfulAt !== null &&
    Date.parse(source.lastSuccessfulAt as string) >
      Date.parse(source.queriedAt as string)
  ) {
    return false;
  }
  if (source.status === 'success')
    return source.error === null && source.lastSuccessfulAt !== null;
  if (source.exhausted) return false;
  return (
    isRecord(source.error) &&
    typeof source.error.code === 'string' &&
    ALERT_ERROR_CODES.has(source.error.code) &&
    typeof source.error.message === 'string'
  );
}

function isAlertResponse(value: unknown): value is CentralizedAlertsResponse {
  if (!isRecord(value) || !hasAlertResponseEnvelope(value)) return false;
  const sources = value.sources as unknown[];
  const sourceIds = new Set<string>();
  for (const source of sources) {
    if (!isRecord(source) || !hasValidSourceShape(source)) return false;
    const page = source as {
      sourceId: string;
      status: 'success' | 'error';
      exhausted: boolean;
    };
    if (sourceIds.has(page.sourceId) || !hasValidSourceState(source))
      return false;
    sourceIds.add(page.sourceId);
  }
  if (!sourceIds.has(alertSourceForScope(value.scope as AlertScope)))
    return false;
  const incomplete = sources.some(source => {
    const page = source as { status: string; exhausted: boolean };
    return page.status !== 'success' || !page.exhausted;
  });
  return incomplete
    ? typeof value.nextCursor === 'string' && value.nextCursor.length > 0
    : value.nextCursor === null;
}

/**
 * Default implementation of the tenant management API
 */
export class TenantManagementApiClient implements TenantManagementApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    try {
      // Try to get the URL through service discovery
      return await this.discoveryApi.getBaseUrl('tenant-management');
    } catch (error) {
      // If service discovery fails, fall back to relative URLs
      // This works in both development and production
      // eslint-disable-next-line no-console
      console.debug('Service discovery failed, using fallback URL:', error);
      return '/api/tenant-management';
    }
  }

  private async getHeaders(): Promise<Record<string, string>> {
    // FetchApi automatically handles authentication
    // We only need to set content-type and correlation ID
    return {
      'Content-Type': 'application/json',
      'X-Correlation-Id': generateCorrelationId(),
    };
  }

  /**
   * Helper to throw ResponseError from a failed response.
   * Uses Backstage's ResponseError for consistent error handling.
   */
  private async throwResponseError(response: Response): Promise<never> {
    throw await ResponseError.fromResponse(response);
  }

  async queryCentralizedAlerts(
    input: AlertQuery,
    signal?: AbortSignal,
  ): Promise<CentralizedAlertsResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}/observability/alerts/query`,
      {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(input),
        signal,
      },
    );
    if (!response.ok) {
      // Authentication middleware and proxies need not use our JSON envelope.
      // HTTP authorization failures must always revoke the caller's cached data.
      if (response.status === 401 || response.status === 403) {
        throw new CentralizedAlertsQueryError(
          response.status === 401 ? 'UNAUTHORIZED' : 'FORBIDDEN',
          response.status,
          response.status === 401
            ? 'Authentication is required to query centralized alerts.'
            : 'You do not have access to centralized alerts.',
        );
      }
      const errorBody: unknown = await response
        .clone()
        .json()
        .catch(() => undefined);
      if (
        isRecord(errorBody) &&
        typeof errorBody.error === 'string' &&
        ALERT_QUERY_ERROR_CODES.has(errorBody.error) &&
        typeof errorBody.message === 'string'
      ) {
        throw new CentralizedAlertsQueryError(
          errorBody.error as CentralizedAlertsQueryErrorCode,
          response.status,
          errorBody.message,
        );
      }
      await this.throwResponseError(response);
    }
    const result: unknown = await response.json();
    if (!isAlertResponse(result)) {
      throw new Error('Invalid centralized alerts response');
    }
    return result;
  }

  async listProducts(): Promise<ProductInfo[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/products`, {
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    const data = await response.json();
    // Backend returns { products: ProductInfo[] }
    return data.products;
  }

  async getProduct(code: ProductCode): Promise<ProductInfo> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/products/${code}`, {
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async listTenants(
    options?: TenantListParams,
  ): Promise<PaginatedResponse<Tenant>> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query params
    const url = new URL(`${baseUrl}/tenants`, window.location.origin);
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }
    if (options?.product) {
      url.searchParams.set('product', options.product);
    }
    if (options?.status) {
      url.searchParams.set('status', options.status);
    }
    // Add enrichment fields if requested (e.g., include=configHealth,upgradeInfo)
    if (options?.include && options.include.length > 0) {
      url.searchParams.set('include', options.include.join(','));
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getTenant(id: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/tenants/${id}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async createTenant(request: CreateTenantRequest): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/tenants`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async updateTenant(
    id: string,
    request: UpdateTenantRequest,
  ): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/tenants/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async deleteTenant(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/tenants/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }
  }

  async deleteDeprovisionedTenant(id: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/delete`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async hardDeleteTenant(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/hard`,
      {
        method: 'DELETE',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }
  }

  async listDeletedTenants(
    options?: PaginationParams,
  ): Promise<PaginatedResponse<Tenant>> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query params
    const url = new URL(`${baseUrl}/tenants/deleted`, window.location.origin);
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getHardDeleteEligibility(id: string): Promise<{
    eligible: boolean;
    reason?: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/hard-delete-eligibility`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async restoreTenant(id: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/restore`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async suspendTenant(id: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/suspend`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async resumeTenant(id: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/resume`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getTenantResources(id: string): Promise<TenantResources> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/resources`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async provisionTenant(id: string, targetVersion?: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/provision`,
      {
        method: 'POST',
        headers,
        body: targetVersion ? JSON.stringify({ targetVersion }) : undefined,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async reprovisionTenant(id: string, targetVersion?: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/reprovision`,
      {
        method: 'POST',
        headers,
        body: targetVersion ? JSON.stringify({ targetVersion }) : undefined,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async retryOperation(
    id: string,
    operation: CanonicalLifecycleOperation,
    versionId?: string,
  ): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const body: Record<string, string> = { operation };
    if (versionId) {
      body.versionId = versionId;
    }

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/retry`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async upgradeTenant(
    id: string,
    targetVersion: string,
    upgradeType?: 'upgrade' | 'rollback' | 'forced' | 'hotfix',
  ): Promise<{
    message: string;
    upgradeId: string;
    tenantId: string;
    fromVersion: string | null;
    toVersion: string;
    status: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/upgrade`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          targetVersion,
          upgradeType: upgradeType || 'upgrade',
        }),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async softwareUpgradeTenant(
    id: string,
    targetVersion: string,
  ): Promise<{
    message: string;
    upgradeId: string;
    tenantId: string;
    fromVersion: string | null;
    toVersion: string;
    status: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/software-upgrade`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          targetVersion,
        }),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getTenantActivity(
    id: string,
    options?: PaginationParams,
  ): Promise<PaginatedResponse<TenantActivity>> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query params
    const url = new URL(
      `${baseUrl}/tenants/${id}/activity`,
      window.location.origin,
    );
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getTenantWorkflowRuns(
    id: string,
    options?: PaginationParams,
  ): Promise<PaginatedResponse<CanonicalLifecycleHistoryItem>> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query params
    const url = new URL(
      `${baseUrl}/tenants/${id}/workflows`,
      window.location.origin,
    );
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async cancelWorkflow(
    id: string,
    forceCancel?: boolean,
  ): Promise<{
    success: boolean;
    message: string;
    warning?: string;
    error?: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/cancel-workflow`,
      {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ forceCancel: forceCancel ?? false }),
      },
    );

    // Even on error, try to parse the response body for error message
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: 'Failed to cancel workflow',
        error: data.error || 'Unknown error',
      };
    }

    return data;
  }

  async getConfig(): Promise<{
    polling: {
      intervalMs: number;
    };
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/config`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) await this.throwResponseError(response);

    return response.json();
  }

  async getRetentionDefaults(): Promise<{
    tierDefaults: Record<string, number>;
    globalDefault: number;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/config/retention-defaults`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) await this.throwResponseError(response);

    return response.json();
  }

  async getPollingStatus(id: string): Promise<{
    tenant: Tenant;
    activities: TenantActivity[];
    timeoutInfo: ProvisioningTimeoutInfo | null;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/polling-status`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getCurrentLifecycle(id: string): Promise<CurrentLifecycleResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/lifecycle/current`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // LIFECYCLE OPERATIONS (Issue #299)
  // ============================================================

  async deprovisionTenant(id: string, reason?: string): Promise<Tenant> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/deprovision`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason }),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async updateRetention(
    id: string,
    request: UpdateRetentionRequest,
  ): Promise<UpdateRetentionResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/retention`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getAuditLog(
    id: string,
    options?: AuditLogQueryOptions,
  ): Promise<AuditLogResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query parameters
    const url = new URL(
      `${baseUrl}/tenants/${id}/audit-log`,
      window.location.origin,
    );
    if (options?.action) {
      url.searchParams.set('action', options.action);
    }
    if (options?.actorId) {
      url.searchParams.set('actorId', options.actorId);
    }
    if (options?.startDate) {
      url.searchParams.set('startDate', options.startDate);
    }
    if (options?.endDate) {
      url.searchParams.set('endDate', options.endDate);
    }
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // VERSION TRACKING (Issue #340, #341)
  // ============================================================

  async getUpgradeHistory(
    id: string,
    options?: UpgradeHistoryOptions,
  ): Promise<UpgradeHistoryResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query parameters
    const url = new URL(
      `${baseUrl}/tenants/${id}/upgrade-history`,
      window.location.origin,
    );
    if (options?.upgradeType) {
      url.searchParams.set('upgradeType', options.upgradeType);
    }
    if (options?.status) {
      url.searchParams.set('status', options.status);
    }
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getVersionSummary(): Promise<{ versions: VersionSummary[] }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/versions/summary`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async listPendingUpgrades(
    options?: PendingUpgradeListOptions,
  ): Promise<PendingUpgradesResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();
    const url = new URL(`${baseUrl}/upgrades/pending`, window.location.origin);

    if (options?.cohort) {
      url.searchParams.set('cohort', options.cohort);
    }
    if (options?.product) {
      url.searchParams.set('product', options.product);
    }
    if (options?.status) {
      url.searchParams.set('status', options.status);
    }
    if (options?.release) {
      url.searchParams.set('release', options.release);
    }
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.cursor) {
      url.searchParams.set('cursor', options.cursor);
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async startBulkUpgrade(
    request: StartBulkUpgradeRequest,
  ): Promise<StartBulkUpgradeResult> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/upgrades/bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getBulkUpgradeJob(jobId: string): Promise<BulkUpgradeJob> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/upgrades/bulk/${encodeURIComponent(jobId)}`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async pauseBulkUpgradeJob(jobId: string): Promise<BulkUpgradeJob> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/upgrades/bulk/${encodeURIComponent(jobId)}/pause`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async resumeBulkUpgradeJob(jobId: string): Promise<BulkUpgradeJob> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/upgrades/bulk/${encodeURIComponent(jobId)}/resume`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async rollbackFailedCohort(
    request: AutoRollbackRequest,
  ): Promise<AutoRollbackResult> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/upgrades/rollback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async syncReleasesFromProduct(productCode: ProductCode): Promise<{
    synced: number;
    skipped: number;
    productCode: ProductCode;
    repository: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases/sync/${productCode}`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async syncAllProductReleases(): Promise<{
    results: Array<{
      synced: number;
      skipped: number;
      productCode: ProductCode;
      repository: string;
      error?: string;
    }>;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/releases/sync`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async listProductReleases(
    productCode: ProductCode,
    limit?: number,
  ): Promise<ApplicationRelease[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const query = limit === undefined ? '' : `?limit=${limit}`;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/products/${productCode}/releases${query}`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async checkUpgradeAvailable(id: string): Promise<{
    upgradeAvailable: boolean;
    currentVersion?: string;
    latestVersion?: string;
    latestReleaseId?: string;
    productCode?: ProductCode;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/upgrade-available`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async verifyTenantVersion(id: string): Promise<{
    tenantId: string;
    databaseVersion: string | null;
    azureVersion: string | null;
    match: boolean;
    driftDescription?: string;
    verifiedResourceId?: string;
    errorMessage?: string;
    verifiedAt: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/verify-version`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getLatestRelease(productCode: ProductCode): Promise<
    | {
        id: string;
        version: string;
        tagName: string;
        releaseName?: string;
        publishedAt: string;
        prerelease: boolean;
      }
    | undefined
  > {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/products/${productCode}/latest-release`,
      {
        method: 'GET',
        headers,
      },
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // MAGIC LINK OPERATIONS (Issue #300)
  // ============================================================

  /**
   * Send a magic link to a tenant administrator for SSO configuration.
   */
  async sendMagicLink(
    id: string,
    recipientEmail: string,
  ): Promise<{
    success: boolean;
    message: string;
    correlationId?: string;
    expiresAt?: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/magic-link`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ recipientEmail }),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getMagicLinkStatus(id: string): Promise<MagicLinkStatus> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/magic-link/status`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async revokeMagicLink(id: string): Promise<RevokeMagicLinkResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/magic-link`,
      {
        method: 'DELETE',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // CONFIGURATION DRIFT DETECTION (Issue #322)
  // ============================================================

  async getConfiguration(id: string): Promise<TenantConfigurationResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/configuration`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async fixConfiguration(
    id: string,
    request: FixConfigurationRequest,
  ): Promise<FixConfigurationResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/configuration`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  async getConfigurationHealthBatch(
    tenantIds: string[],
  ): Promise<ConfigurationHealthBatchResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/configuration/health`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ tenantIds }),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // ADMIN OPERATIONS
  // ============================================================

  /**
   * Admin reset of tenant status.
   * Emergency operation to recover stuck tenants.
   */
  async adminResetTenantStatus(
    id: string,
    reason: string,
  ): Promise<{
    success: boolean;
    tenant: Tenant;
    message: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/admin/reset-status`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ targetStatus: 'Failed', reason }),
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // HEALTH MONITORING (Issue #359)
  // ============================================================

  /**
   * Get current health status for a tenant.
   */
  async getHealthStatus(id: string): Promise<TenantHealthStatus | null> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/health`,
      {
        method: 'GET',
        headers,
      },
    );

    // 404 means no health status exists yet
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  /**
   * Get health check history for a tenant.
   */
  async getHealthHistory(
    id: string,
    options?: {
      limit?: number;
      offset?: number;
      checkType?: HealthCheckType;
    },
  ): Promise<CommonPaginatedResponse<TenantHealthCheck>> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query parameters
    const url = new URL(
      `${baseUrl}/tenants/${id}/health/history`,
      window.location.origin,
    );
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }
    if (options?.checkType) {
      url.searchParams.set('checkType', options.checkType);
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  /**
   * Get resource-level health status for a tenant.
   */
  async getResourceHealthStatus(id: string): Promise<ResourceHealthStatus[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/health/resources`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    const data = await response.json();
    // Validate response structure
    if (!data || !Array.isArray(data.resources)) {
      return [];
    }
    return data.resources;
  }

  /** Get the latest PLT S2S service-health snapshot for Tenant Details. */
  async getPltS2SServiceHealth(
    id: string,
  ): Promise<PltS2SServiceHealthResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/health/plt-services`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  /** Get the capability, latest data-status snapshot, and readiness rollup. */
  async getPltDataStatus(id: string): Promise<PltDataStatusResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/health/data-status`,
      { method: 'GET', headers },
    );
    if (!response.ok) {
      await this.throwResponseError(response);
    }
    return response.json();
  }

  /**
   * Get one bounded, live page of PLT backfill/reconciliation evidence.
   * Nothing is cached by the Portal; follow `page.nextCursor` until null.
   */
  async getPltDataOperations(
    id: string,
    query: PltDataOperationsQuery,
  ): Promise<PltDataOperationsResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();
    const params = new URLSearchParams({ producer: query.producer });
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.windowHours !== undefined) {
      params.set('windowHours', String(query.windowHours));
    }
    if (query.cursor !== undefined) params.set('cursor', query.cursor);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/health/data-operations?${params.toString()}`,
      { method: 'GET', headers },
    );
    if (!response.ok) {
      await this.throwResponseError(response);
    }
    return response.json();
  }

  /**
   * Trigger a manual health check for a tenant.
   */
  async triggerHealthCheck(id: string): Promise<TenantHealthStatus> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/health/check`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  /**
   * Refresh health checks for all active tenants.
   */
  async refreshAllHealthChecks(): Promise<{
    success: boolean;
    message: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/health/refresh`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  // ============================================================
  // COST MONITORING (Issue #271)
  // ============================================================

  /**
   * Get current cost summary for a tenant.
   */
  async getCostSummary(id: string): Promise<TenantCostSummary | null> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/cost`,
      {
        method: 'GET',
        headers,
      },
    );

    // 404 means no cost data exists yet
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  /**
   * Get cost history for a tenant.
   */
  async getCostHistory(
    id: string,
    options?: CostHistoryOptions,
  ): Promise<CostHistoryResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    // Build URL with query parameters
    const url = new URL(
      `${baseUrl}/tenants/${id}/cost/history`,
      window.location.origin,
    );
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }
    if (options?.startDate) {
      url.searchParams.set('startDate', options.startDate);
    }
    if (options?.endDate) {
      url.searchParams.set('endDate', options.endDate);
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }

  /**
   * Trigger a manual cost refresh for a tenant.
   */
  async refreshTenantCosts(id: string): Promise<TenantCostSummary> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/tenants/${id}/cost/refresh`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      await this.throwResponseError(response);
    }

    return response.json();
  }
}
